"use client";
import { useState, useEffect } from "react";
import Image from "next/image";

const TEAL = "#4FCDC4";

function CarouselChevron({ direction, onClick }) {
  const [hover, setHover] = useState(false);
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={isPrev ? "Previous screenshot" : "Next screenshot"}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [isPrev ? "left" : "right"]: 16,
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: hover ? "rgba(15,15,17,0.92)" : "rgba(15,15,17,0.7)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        color: hover ? "#ffffff" : "rgba(255,255,255,0.85)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.15s ease, color 0.15s ease",
        zIndex: 2,
        padding: 0,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {isPrev ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
      </svg>
    </button>
  );
}

export default function FlagshipCarousel({ images, intervalMs = 4500 }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [paused, images.length, intervalMs]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div style={{
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.45), 0 6px 20px rgba(0,0,0,0.35)",
        background: "#0f0f11",
      }}>
        <div style={{
          height: 36,
          background: "rgba(15,15,17,0.95)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }} aria-hidden="true">
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57" }} />
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#febc2e" }} />
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840" }} />
          </div>
        </div>
        <div style={{ position: "relative", width: "100%", aspectRatio: "2940 / 1610" }}>
          {images.map((img, i) => (
            <Image
              key={img.src}
              src={img.src}
              alt={img.alt}
              fill
              priority={i === 0}
              sizes="(max-width: 1200px) 100vw, 1200px"
              style={{
                opacity: activeIndex === i ? 1 : 0,
                transition: "opacity 0.7s ease-in-out",
                objectFit: "cover",
                objectPosition: "top",
              }}
            />
          ))}
          {images.length > 1 && (
            <>
              <CarouselChevron
                direction="prev"
                onClick={() => setActiveIndex((i) => (i - 1 + images.length) % images.length)}
              />
              <CarouselChevron
                direction="next"
                onClick={() => setActiveIndex((i) => (i + 1) % images.length)}
              />
            </>
          )}
        </div>
      </div>

      {images.length > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          marginTop: 20,
        }}>
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`Show flagship screenshot ${i + 1}: ${img.alt?.slice(0, 60) || ""}`}
              style={{
                width: activeIndex === i ? 40 : 24,
                height: 24,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "width 0.3s ease",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "block",
                  width: activeIndex === i ? 24 : 8,
                  height: 8,
                  borderRadius: 999,
                  background: activeIndex === i ? TEAL : "rgba(255,255,255,0.18)",
                  transition: "width 0.3s ease, background 0.2s ease",
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
